import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Users,
  Building2,
  Briefcase,
  CheckCircle,
  Settings,
  BarChart3,
  ShieldCheck,
  ScrollText,
  Shield,
  Store,
  FileCheck,
  Tags,
  Handshake,
  Code2,
  ShoppingCart,
  Package,
  User,
  Percent,
  CreditCard,
  FolderTree,
  
  Zap,
  Coins,
  Wallet,
  MessageSquare,
  Plug,
  Bell,
  Mail,
  Map,
  BookOpen,
  Wrench,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface MenuItem {
  id: string;
  label: string;
  icon: any;
  path: string;
}

interface MenuCategory {
  title: string;
  items: MenuItem[];
}

const menuCategories: MenuCategory[] = [
  {
    title: "OVERVIEW",
    items: [
      {
        id: "dashboard",
        label: "Dashboard",
        icon: LayoutDashboard,
        path: "/admin/dashboard",
      },
      {
        id: "roadmap",
        label: "Roadmap",
        icon: Map,
        path: "/admin/roadmap",
      },
      {
        id: "chief-of-staff",
        label: "Chief of Staff",
        icon: Zap,
        path: "/admin/chief-of-staff",
      },
      {
        id: "analytics",
        label: "Analytics",
        icon: BarChart3,
        path: "/admin/analytics",
      },
    ],
  },
  {
    title: "MANAGEMENT",
    items: [
      {
        id: "users",
        label: "Users",
        icon: Users,
        path: "/admin/users",
      },
      {
        id: "companies",
        label: "Companies",
        icon: Building2,
        path: "/admin/companies",
      },
      // Jobs deactivated for personal-only MVP
      // {
      //   id: "jobs",
      //   label: "Jobs",
      //   icon: Briefcase,
      //   path: "/admin/jobs",
      // },
      {
        id: "industry-categories",
        label: "Industry Categories",
        icon: FolderTree,
        path: "/admin/industry-categories",
      },
      {
        id: "integrations",
        label: "Integrations",
        icon: Plug,
        path: "/admin/integrations",
      },
    ],
  },
  {
    title: "MARKETPLACE",
    items: [
      {
        id: "modules",
        label: "Modules",
        icon: Package,
        path: "/admin/modules",
      },
      {
        id: "marketplace-categories",
        label: "Categories",
        icon: Tags,
        path: "/admin/marketplace-categories",
      },
      {
        id: "partnerships",
        label: "Partnerships",
        icon: Handshake,
        path: "/admin/partnerships",
      },
      {
        id: "tech-stack",
        label: "Tech Stack Registry",
        icon: Code2,
        path: "/admin/tech-stack",
      },
    ],
  },
  {
    title: "ECOMMERCE",
    items: [
      {
        id: "orders",
        label: "Orders",
        icon: Package,
        path: "/admin/commerce/orders",
      },
      {
        id: "hiring-tokens",
        label: "Hiring Tokens",
        icon: Coins,
        path: "/admin/commerce/hiring-tokens",
      },
      {
        id: "company-wallets",
        label: "Company Wallets",
        icon: Wallet,
        path: "/admin/commerce/wallets",
      },
      // Employer Products deactivated for personal-only MVP
      // {
      //   id: "employer-products",
      //   label: "Employer Products",
      //   icon: Briefcase,
      //   path: "/admin/commerce/employer-products",
      // },
      {
        id: "jobseeker-products",
        label: "Job Seeker Products",
        icon: User,
        path: "/admin/commerce/jobseeker-products",
      },
      {
        id: "discounts",
        label: "Discounts",
        icon: Percent,
        path: "/admin/commerce/discounts",
      },
      {
        id: "payment-methods",
        label: "Payment Methods",
        icon: CreditCard,
        path: "/admin/commerce/payment-methods",
      },
    ],
  },
  {
    title: "COMMUNICATION",
    items: [
      {
        id: "sms-management",
        label: "SMS Management",
        icon: MessageSquare,
        path: "/admin/sms",
      },
      {
        id: "notifications",
        label: "In-App Notifications",
        icon: Bell,
        path: "/admin/notifications",
      },
      {
        id: "email-management",
        label: "Email Management",
        icon: Mail,
        path: "/admin/email",
      },
    ],
  },
  {
    title: "KNOWLEDGE",
    items: [
      {
        id: "documentation",
        label: "Documentation",
        icon: BookOpen,
        path: "/admin/documents",
      },
    ],
  },
  {
    title: "SYSTEM",
    items: [
      // Employer Requests deactivated for personal-only MVP
      // {
      //   id: "employer-requests",
      //   label: "Employer Requests",
      //   icon: Briefcase,
      //   path: "/admin/employer-requests",
      // },
      {
        id: "tool-monitor",
        label: "Tool Monitor",
        icon: Wrench,
        path: "/admin/tool-monitor",
      },
      {
        id: "qa-dashboard",
        label: "QA Dashboard",
        icon: Shield,
        path: "/admin/qa-dashboard",
      },
      {
        id: "ai-management",
        label: "AI Management",
        icon: Zap,
        path: "/admin/ai",
      },
      {
        id: "system-health",
        label: "System Health",
        icon: ShieldCheck,
        path: "/admin/system-health",
      },
      {
        id: "webhook-testing",
        label: "Webhook Testing",
        icon: Zap,
        path: "/admin/webhook-testing",
      },
      {
        id: "app-store",
        label: "App Store",
        icon: Package,
        path: "/admin/app-store",
      },
      {
        id: "settings",
        label: "Settings",
        icon: Settings,
        path: "/admin/settings",
      },
    ],
  },
];

export const AdminSidebar = () => {
  const location = useLocation();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  // Fetch pending approvals count for badge
  const { data: pendingApprovals } = useQuery({
    queryKey: ["admin-pending-approvals"],
    queryFn: async () => {
      const { count } = await supabase
        .from("company_admin_requests")
        .select("*", { count: "exact", head: true })
        .eq("status", "pending");

      return count || 0;
    },
    staleTime: 30 * 1000,
  });

  const isActiveRoute = (path: string) => {
    return path === location.pathname;
  };

  const renderMenuItem = (item: MenuItem) => {
    const isActive = isActiveRoute(item.path);
    const isPendingApprovals = item.id === "employer-requests" && pendingApprovals && pendingApprovals > 0;

    return (
      <SidebarMenuItem key={item.id}>
        <SidebarMenuButton asChild tooltip={isCollapsed ? item.label : undefined}>
          <NavLink
            to={item.path}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200",
                "hover:bg-sidebar-accent/50",
                isActive
                  ? "bg-gradient-to-r from-blue-500/10 to-blue-500/5 text-blue-600 font-medium"
                  : "text-sidebar-foreground"
              )
            }
          >
            <item.icon
              className={cn(
                "h-5 w-5 transition-colors",
                isActive ? "text-blue-600" : "text-muted-foreground"
              )}
            />
            {!isCollapsed && (
              <>
                <span className="text-sm flex-1">{item.label}</span>
                {isPendingApprovals && (
                  <Badge 
                    variant="default" 
                    className="bg-blue-500 text-white rounded-full px-2 py-0.5 text-xs font-semibold"
                  >
                    {pendingApprovals}
                  </Badge>
                )}
              </>
            )}
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarContent>
        {/* Header */}
        <div className="px-4 py-6">
          <div className="flex items-center gap-3">
            <div className="bg-blue-500/10 p-2 rounded-xl">
              <ShieldCheck className="h-6 w-6 text-blue-600" />
            </div>
            {!isCollapsed && (
              <div>
                <h2 className="text-lg font-bold tracking-tight">Admin Panel</h2>
                <p className="text-xs text-muted-foreground">System Management</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Categories */}
        {menuCategories.map((category, index) => (
          <SidebarGroup key={index}>
            {!isCollapsed && (
              <SidebarGroupLabel className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {category.title}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {category.items.map((item) => renderMenuItem(item))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
};
